import React, {
  useState,
  useEffect,
  useCallback,
} from 'react';
import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useMutation, useQuery } from '@apollo/client';
import { captureException, useFeatureFlags } from '@playbookux/playbook-web-core';
import withLayout from '../../../core/components/with-layout';

import {
  DELETE_TEMPLATE,
  updateTemplateDeleted,
} from '../../../core/graphql/templates';

import {
  TEMPLATE_TYPES,
  BUTTON_TEXT_MAP,
  TITLE_MAP,
  DESCRIPTION_MAP,
  NO_ITEMS_MESSAGE_MAP,
} from './constants';

import {
  Container,
  ContainerMenu,
  Menu,
  MenuItem,
} from './styles';
import GenericTemplateManagementComponent from './components/template';
import GET_CURRENT_COMPANY_MEMBER from './graphql';

const TemplateLibrary = (props) => {
  const {
    history,
    location: { hash },
    companyId,
    user: {
      id: userId,
      admin: userIsAdmin,
    },
  } = props;

  const activeTab = TEMPLATE_TYPES[hash.replace('#', '').toUpperCase()] || TEMPLATE_TYPES.TEST;

  const [
    deleteTemplate,
    {
      loading: loadingDelete,
      error: errorDelete,
    },
  ] = useMutation(DELETE_TEMPLATE);

  const {
    data = {},
  } = useQuery(GET_CURRENT_COMPANY_MEMBER, {
    variables: {
      userId,
      companyId,
    },
    skip: !userId || !companyId,
  });

  const role = !isEmpty(data) && data?.companyMembers[0]?.role;
  const isAdmin = userIsAdmin || role === 'admin';

  const [activeTemplateType, setActiveTemplateType] = useState(activeTab);

  const handleDeleteTemplate = useCallback(async (templateId) => {
    if (templateId) {
      try {
        await deleteTemplate(
          {
            variables: {
              id: templateId,
            },
            update: updateTemplateDeleted(templateId, activeTemplateType.toLowerCase(), companyId),
          },
        );
      } catch (error) {
        captureException('DELETE_TEMPLATE_ERROR', error);
      }
    }
  }, [activeTemplateType, companyId, deleteTemplate]);

  const handleCreateTemplate = useCallback(() => {
    if (activeTemplateType === TEMPLATE_TYPES.TEST) {
      history.push('/template-library/create-questions');
    } else {
      history.push('/template-library/create-audience');
    }
  }, [activeTemplateType, history]);

  const handleEditTemplate = useCallback((templateId) => {
    if (activeTemplateType === TEMPLATE_TYPES.TEST) {
      history.push(`/template-library/${templateId}/edit-questions`);
    }
    if (activeTemplateType === TEMPLATE_TYPES.AUDIENCE) {
      history.push(`/template-library/${templateId}/edit-audience`);
    }
  }, [activeTemplateType, history]);

  const handleItemClick = (e, { name }) => {
    setActiveTemplateType(name);
  };

  const {
    featureFlags: { consentForms: consentFormsFeatureFlag },
    loading: loadingFeatureFlags,
  } = useFeatureFlags(companyId);

  useEffect(() => {
    window.location.hash = activeTemplateType;
  }, [activeTemplateType]);

  const showConsentComponent = !loadingFeatureFlags && activeTemplateType === TEMPLATE_TYPES.CONSENT;

  return (
    <>
      <Container>
        <ContainerMenu>
          <Menu secondary>
            <MenuItem
              name={TEMPLATE_TYPES.TEST}
              active={activeTemplateType === TEMPLATE_TYPES.TEST}
              onClick={handleItemClick}
            >
              Test Templates
            </MenuItem>
            <MenuItem
              name={TEMPLATE_TYPES.AUDIENCE}
              active={activeTemplateType === TEMPLATE_TYPES.AUDIENCE}
              onClick={handleItemClick}
            >
              Audience Templates
            </MenuItem>
            {
              isAdmin
              && consentFormsFeatureFlag
              && (
                <MenuItem
                  name={TEMPLATE_TYPES.CONSENT}
                  active={activeTemplateType === TEMPLATE_TYPES.CONSENT}
                  onClick={handleItemClick}
                >
                  Consent Templates
                </MenuItem>
              )
            }
          </Menu>
        </ContainerMenu>
        <GenericTemplateManagementComponent
          title={TITLE_MAP[activeTemplateType]}
          description={DESCRIPTION_MAP[activeTemplateType]}
          buttonText={BUTTON_TEXT_MAP[activeTemplateType]}
          onCreateTemplate={handleCreateTemplate}
          noItemsMessage={NO_ITEMS_MESSAGE_MAP[activeTemplateType]}
          templateType={activeTemplateType}
          onEditTemplate={handleEditTemplate}
          onDeleteTemplate={handleDeleteTemplate}
          deleteTemplateError={errorDelete}
          deleteTemplateLoading={loadingDelete}
          headerButtonText={BUTTON_TEXT_MAP[activeTemplateType]}
          headerDescription={DESCRIPTION_MAP[activeTemplateType]}
          headerTitle={TITLE_MAP[activeTemplateType]}
          showConsentComponent={showConsentComponent}
        />
      </Container>
    </>
  );
};

TemplateLibrary.propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func,
  }).isRequired,
  location: PropTypes.shape({
    hash: PropTypes.string,
  }),
  companyId: PropTypes.string.isRequired,
  user: PropTypes.shape({
    id: PropTypes.string,
    admin: PropTypes.bool,
  }),
};

TemplateLibrary.defaultProps = {
  location: PropTypes.shape({
    hash: TEMPLATE_TYPES.TEST,
  }),
  user: {
    id: '',
    admin: false,
  },
};

const mapStateToProps = ({ authentication }) => ({
  companyId: authentication.company?.id,
  user: authentication.user,
});

const enhance = compose(
  withLayout({ selectedTab: null }),
  connect(mapStateToProps, null),
);

export default enhance(TemplateLibrary);
